import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { CirclePlus, Edit, Clock, Menu, Download, ChevronDown, FileText, FileSpreadsheet, Star } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setApiErrorJson, setApiJson } from '../../redux/Actions/ApiAction';
import { getSelectedUnit } from '../../storage/Storage';
import { deleteChild, deleteParent, searchChild, searchParent, addSalonParent, searchSalonParent, addSalonChild, searchSalonChild, addKiosParent, searchKiosParent, addKiosChild, searchKiosChild, addBestService, searchBestService, addAtHomeParent, searchAtHomeParent, addAtHomeChild, searchAtHomeChild } from '../../constant/Constant';
import { HitApi } from '../../Api/ApiHit';
import PageHeader from '../../PageHeader/PageHeader';
import EditButton from '../../components/Edit/EditButton';
import DeleteButton from '../../components/Delete/DeleteButton';
import GroupPagination from '../../components/Pagination/GroupPagination';
import { setGroupPagination } from '../../redux/Actions/GroupPaginationAction';
import ServicePagination from '../../components/Pagination/ServicePagination';
import { setServicePagination } from '../../redux/Actions/ServicePaginationAction';
import toast from 'react-hot-toast';
import { hasPermission } from '../../utils/permissionUtils';
import DeleteConfirmationModal from '../../components/CustomModal/DeleteConfirmationModal';
import AddSalonGroupButton from '../../components/AddSalonGroupButton/AddSalonGroupButton';
import AddSalonChildButton from '../../components/AddSalonChildButton/AddSalonChildButton';
import AddKiosGroupButton from '../../components/AddKiosGroupButton/AddKiosGroupButton';
import AddKiosChildButton from '../../components/AddKiosChildButton/AddKiosChildButton';
import AddAtHomeGroupButton from '../../components/AddAtHomeGroupButton/AddAtHomeGroupButton';
import AddAtHomeChildButton from '../../components/AddAtHomeChildButton/AddAtHomeChildButton';
import { exportToCSV, exportToPDF } from '../../utils/exportUtils';

function Services() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const ApiReducer = useSelector((state) => state.ApiReducer);
    const GroupPaginationReducer = useSelector((state) => state.GropPaginationReducer);
    const ServicePaginationReducer = useSelector((state) => state.ServicePaginationReducer);

    const [parentService, setParentService] = useState();
    const [childService, setChildService] = useState();
    const [parentId, setParentId] = useState('');
    const [selectedParent, setSelectedParent] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletingRow, setDeletingRow] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Salon group tracking - Map of name -> _id
    const [salonParentNames, setSalonParentNames] = useState(new Set());
    const [salonParentMap, setSalonParentMap] = useState(new Map());

    // Salon child tracking
    const [salonChildIds, setSalonChildIds] = useState(new Set());

    // Kios group tracking - Map of name -> _id
    const [kiosParentNames, setKiosParentNames] = useState(new Set());
    const [kiosParentMap, setKiosParentMap] = useState(new Map());

    // Kios child tracking
    const [kiosChildIds, setKiosChildIds] = useState(new Set());

    // AtHome group tracking - Map of name -> _id
    const [atHomeParentNames, setAtHomeParentNames] = useState(new Set());
    const [atHomeParentMap, setAtHomeParentMap] = useState(new Map());

    // AtHome child tracking
    const [atHomeChildIds, setAtHomeChildIds] = useState(new Set());

    // Best service tracking
    const [bestServiceNames, setBestServiceNames] = useState(new Set());

    // Export dropdown state
    const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
    const exportDropdownRef = useRef(null);

    // const canEditParent = hasPermission('services', 'write', 'parent');
    // const canReadParent = hasPermission('services', 'read', 'parent');
    // const canDeleteParent = hasPermission('services', 'delete', 'parent');
    // const canAddParent = hasPermission('services', 'write', 'parent');

    // const canEditChild = hasPermission('services', 'write', 'child');
    // const canReadChild = hasPermission('services', 'read', 'child');
    // const canDeleteChild = hasPermission('services', 'delete', 'child');
    // const canAddChild = hasPermission('services', 'write', 'child');


    const canEditSerive = hasPermission('services', 'write');
    const canReadService = hasPermission('services', 'read');
    const canDeleteService = hasPermission('services', 'delete');
    const canAddService = hasPermission('services', 'write');

    useEffect(() => {
        getServiceGroup();
        fetchSalonParents();
        fetchSalonChildren();
        fetchKiosParents();
        fetchKiosChildren();
        fetchAtHomeParents();
        fetchAtHomeChildren();
        fetchBestServices();
    }, [GroupPaginationReducer.pagination.page, GroupPaginationReducer.pagination.limit]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
                setIsExportDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (selectedParent) {
            showSelectedChild(selectedParent);
        }
    }, [ServicePaginationReducer.pagination.page, ServicePaginationReducer.pagination.limit]);

    const fetchSalonParents = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchSalonParent).then((res) => {
            if (res?.data?.docs) {
                // Use lowercase for case-insensitive matching
                const names = new Set(res.data.docs.map((doc) => doc.name?.toLowerCase()?.trim()));
                setSalonParentNames(names);
                // Create map of name -> _id for finding SalonParent ID
                const parentMap = new Map();
                res.data.docs.forEach((doc) => {
                    parentMap.set(doc.name?.toLowerCase()?.trim(), doc._id);
                });
                setSalonParentMap(parentMap);
            }
        }).catch((err) => {
            console.error('Error fetching salon parents:', err);
        });
    };

    const fetchSalonChildren = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchSalonChild).then((res) => {
            if (res?.data?.docs) {
                // Use lowercase for case-insensitive matching
                const names = new Set(res.data.docs.map((doc) => doc.name?.toLowerCase()?.trim()));
                setSalonChildIds(names);
            }
        }).catch((err) => {
            console.error('Error fetching salon children:', err);
        });
    };

    const fetchKiosParents = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchKiosParent).then((res) => {
            if (res?.data?.docs) {
                // Use lowercase for case-insensitive matching
                const names = new Set(res.data.docs.map((doc) => doc.name?.toLowerCase()?.trim()));
                setKiosParentNames(names);
                // Create map of name -> _id for finding KiosParent ID
                const parentMap = new Map();
                res.data.docs.forEach((doc) => {
                    parentMap.set(doc.name?.toLowerCase()?.trim(), doc._id);
                });
                setKiosParentMap(parentMap);
            }
        }).catch((err) => {
            console.error('Error fetching kios parents:', err);
        });
    };

    const fetchKiosChildren = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchKiosChild).then((res) => {
            if (res?.data?.docs) {
                // Use lowercase for case-insensitive matching
                const names = new Set(res.data.docs.map((doc) => doc.name?.toLowerCase()?.trim()));
                setKiosChildIds(names);
            }
        }).catch((err) => {
            console.error('Error fetching kios children:', err);
        });
    };

    const fetchAtHomeParents = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchAtHomeParent).then((res) => {
            if (res?.data?.docs) {
                const names = new Set(res.data.docs.map((doc) => doc.name?.toLowerCase()?.trim()));
                setAtHomeParentNames(names);
                const parentMap = new Map();
                res.data.docs.forEach((doc) => {
                    parentMap.set(doc.name?.toLowerCase()?.trim(), doc._id);
                });
                setAtHomeParentMap(parentMap);
            }
        }).catch((err) => {
            console.error('Error fetching at home parents:', err);
        });
    };

    const fetchAtHomeChildren = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchAtHomeChild).then((res) => {
            if (res?.data?.docs) {
                const names = new Set(res.data.docs.map((doc) => doc.name?.toLowerCase()?.trim()));
                setAtHomeChildIds(names);
            }
        }).catch((err) => {
            console.error('Error fetching at home children:', err);
        });
    };

    const fetchBestServices = () => {
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchBestService).then((res) => {
            if (res?.data?.docs) {
                // Use lowercase for case-insensitive matching
                const names = new Set(res.data.docs.map((doc) => doc.name?.toLowerCase()?.trim()));
                setBestServiceNames(names);
            }
        }).catch((err) => {
            console.error('Error fetching best services:', err);
        });
    };

    const getServiceGroup = () => {
        const json = {
            page: GroupPaginationReducer.pagination.page,
            limit: GroupPaginationReducer.pagination.limit,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchParent).then((res) => {
            console.log("res_____+_+", res);

            if (res?.data?.docs) {
                setParentService(res.data?.docs);
                dispatch(setGroupPagination({
                    total: res?.data?.totalDocs,
                    page: GroupPaginationReducer.pagination.page,
                    limit: GroupPaginationReducer.pagination.limit
                }));
            } else {
                setParentService([]);
                toast.error('Failed to fetch service groups. Please try again.');
            }
        }).catch((err) => {
            setParentService([]);
            toast.error('Error fetching service groups. Please try again.');
            console.error(err);
        });
    };

    const showSelectedChild = (parent) => {
        setParentId(parent?._id);
        setSelectedParent(parent);

        const json = {
            page: ServicePaginationReducer.pagination.page,
            limit: ServicePaginationReducer.pagination.limit,
            search: { parentId: parent?._id }
        };

        HitApi(json, searchChild).then((res) => {
            if (res?.data?.docs) {
                dispatch(setServicePagination({
                    total: res?.data?.totalDocs || 0,
                    page: ServicePaginationReducer.pagination.page,
                    limit: ServicePaginationReducer.pagination.limit
                }));
                setChildService(res.data?.docs);
            } else {
                setChildService([]);
                toast.error('Failed to fetch child services. Please try again.');
            }
        }).catch((err) => {
            setChildService([]);
            toast.error('Error fetching child services. Please try again.');
            console.error(err);
        });
    };

    const handleEdit = (row) => {
        if (row?._id) navigate(`/parentservice/editparentservice/${row._id}`);
        else toast.error('Unable to edit: Service group ID not found');
    };

    const handleEditChild = (row) => {
        if (row?._id) navigate(`/editChild/${row._id}`);
        else toast.error('Unable to edit: Service ID not found');
    };

    const handleDelete = (row) => {
        setDeletingRow(row);
        setIsDeleteModalOpen(true);
    };

    const handleAddToSalonGroup = (parent) => {
        const json = {
            name: parent?.name,
            parentDesc: parent?.parentDesc || '',
            unitIds: parent?.unitIds || (getSelectedUnit()?._id ? [getSelectedUnit()._id] : getSelectedUnit()?.[0] ? [getSelectedUnit()[0]] : [])
        };

        HitApi(json, addSalonParent).then((res) => {
            if (res?.success || res?.statusCode === 201) {
                toast.success('Added to Salon Group successfully!');
                // Use lowercase for case-insensitive matching
                setSalonParentNames((prev) => new Set([...prev, parent?.name?.toLowerCase()?.trim()]));
                // Also update salonParentMap with new parent's _id
                const newParentId = res?.data?._id || res?._id;
                if (newParentId) {
                    setSalonParentMap((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(parent?.name?.toLowerCase()?.trim(), newParentId);
                        return newMap;
                    });
                }
            } else {
                toast.error(res?.message || 'Failed to add to Salon Group');
            }
        }).catch((err) => {
            toast.error('Error adding to Salon Group. Please try again.');
            console.error(err);
        });
    };

    const handleAddToSalonChild = (child) => {
        // Get SalonParent's _id by matching the selectedParent's name (case-insensitive)
        const salonParentId = salonParentMap.get(selectedParent?.name?.toLowerCase()?.trim());

        if (!salonParentId) {
            toast.error('Please add parent to Salon Group first');
            return;
        }

        const json = {
            name: child?.name,
            parentId: salonParentId,
            staffIds: child?.staffIds?.map(s => s?._id || s) || [],
            unitIds: child?.unitIds?._id || child?.unitIds || getSelectedUnit()?._id || getSelectedUnit()?.[0],
            service_time: child?.service_time,
            price: child?.price,
            member_price: child?.member_price,
            img: child?.img,
            childDesc: child?.childDesc,
            incentive: child?.incentive,
            gender: child?.gender,
            products: child?.steps?.flatMap(step =>
                step?.products?.map(p => ({
                    product: p?.product,
                    quantity: parseFloat(p?.quantity) || 0,
                    unit: p?.productType || ''
                })) || []
            ) || []
        };

        HitApi(json, addSalonChild).then((res) => {
            if (res?.success || res?.statusCode === 201) {
                toast.success('Added to Salon Child successfully!');
                // Use lowercase for case-insensitive matching
                setSalonChildIds((prev) => new Set([...prev, child?.name?.toLowerCase()?.trim()]));
            } else {
                toast.error(res?.message || 'Failed to add to Salon Child');
            }
        }).catch((err) => {
            toast.error('Error adding to Salon Child. Please try again.');
            console.error(err);
        });
    };

    const handleAddToKiosGroup = (parent) => {
        const json = {
            name: parent?.name,
            parentDesc: parent?.parentDesc || '',
            unitIds: parent?.unitIds || (getSelectedUnit()?._id ? [getSelectedUnit()._id] : getSelectedUnit()?.[0] ? [getSelectedUnit()[0]] : [])
        };

        HitApi(json, addKiosParent).then((res) => {
            if (res?.success || res?.statusCode === 201) {
                toast.success('Added to Kios Group successfully!');
                // Use lowercase for case-insensitive matching
                setKiosParentNames((prev) => new Set([...prev, parent?.name?.toLowerCase()?.trim()]));
                // Also update kiosParentMap with new parent's _id
                const newParentId = res?.data?._id || res?._id;
                if (newParentId) {
                    setKiosParentMap((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(parent?.name?.toLowerCase()?.trim(), newParentId);
                        return newMap;
                    });
                }
            } else {
                toast.error(res?.message || 'Failed to add to Kios Group');
            }
        }).catch((err) => {
            toast.error('Error adding to Kios Group. Please try again.');
            console.error(err);
        });
    };

    const handleAddToKiosChild = (child) => {
        // Get KiosParent's _id by matching the selectedParent's name (case-insensitive)
        const kiosParentId = kiosParentMap.get(selectedParent?.name?.toLowerCase()?.trim());

        if (!kiosParentId) {
            toast.error('Please add parent to Kios Group first');
            return;
        }

        const json = {
            name: child?.name,
            parentId: kiosParentId,
            staffIds: child?.staffIds?.map(s => s?._id || s) || [],
            unitIds: child?.unitIds?._id || child?.unitIds || getSelectedUnit()?._id || getSelectedUnit()?.[0],
            service_time: child?.service_time,
            price: child?.price,
            member_price: child?.member_price,
            img: child?.img,
            childDesc: child?.childDesc,
            incentive: child?.incentive,
            gender: child?.gender,
            products: child?.steps?.flatMap(step =>
                step?.products?.map(p => ({
                    product: p?.product,
                    quantity: parseFloat(p?.quantity) || 0,
                    unit: p?.productType || ''
                })) || []
            ) || []
        };

        HitApi(json, addKiosChild).then((res) => {
            if (res?.success || res?.statusCode === 201) {
                toast.success('Added to Kios Child successfully!');
                // Use lowercase for case-insensitive matching
                setKiosChildIds((prev) => new Set([...prev, child?.name?.toLowerCase()?.trim()]));
            } else {
                toast.error(res?.message || 'Failed to add to Kios Child');
            }
        }).catch((err) => {
            toast.error('Error adding to Kios Child. Please try again.');
            console.error(err);
        });
    };

    const handleAddToAtHomeGroup = (parent) => {
        const json = {
            name: parent?.name,
            parentDesc: parent?.parentDesc || '',
            unitIds: parent?.unitIds || (getSelectedUnit()?._id ? [getSelectedUnit()._id] : getSelectedUnit()?.[0] ? [getSelectedUnit()[0]] : [])
        };

        HitApi(json, addAtHomeParent).then((res) => {
            if (res?.success || res?.statusCode === 201) {
                toast.success('Added to Home Service Group successfully!');
                setAtHomeParentNames((prev) => new Set([...prev, parent?.name?.toLowerCase()?.trim()]));
                const newParentId = res?.data?._id || res?._id;
                if (newParentId) {
                    setAtHomeParentMap((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(parent?.name?.toLowerCase()?.trim(), newParentId);
                        return newMap;
                    });
                }
            } else {
                toast.error(res?.message || 'Failed to add to Home Service Group');
            }
        }).catch((err) => {
            toast.error('Error adding to Home Service Group. Please try again.');
            console.error(err);
        });
    };

    const handleAddToAtHomeChild = (child) => {
        const atHomeParentId = atHomeParentMap.get(selectedParent?.name?.toLowerCase()?.trim());

        if (!atHomeParentId) {
            toast.error('Please add parent to Home Service Group first');
            return;
        }

        const json = {
            name: child?.name,
            parentId: atHomeParentId,
            staffIds: child?.staffIds?.map(s => s?._id || s) || [],
            unitIds: child?.unitIds?._id || child?.unitIds || getSelectedUnit()?._id || getSelectedUnit()?.[0],
            service_time: child?.service_time,
            price: child?.price,
            member_price: child?.member_price,
            img: child?.img,
            childDesc: child?.childDesc,
            incentive: child?.incentive,
            gender: child?.gender,
            products: child?.steps?.flatMap(step =>
                step?.products?.map(p => ({
                    product: p?.product,
                    quantity: parseFloat(p?.quantity) || 0,
                    unit: p?.productType || ''
                })) || []
            ) || []
        };

        HitApi(json, addAtHomeChild).then((res) => {
            if (res?.success || res?.statusCode === 201) {
                toast.success('Added to Home Service Child successfully!');
                setAtHomeChildIds((prev) => new Set([...prev, child?.name?.toLowerCase()?.trim()]));
            } else {
                toast.error(res?.message || 'Failed to add to Home Service Child');
            }
        }).catch((err) => {
            toast.error('Error adding to Home Service Child. Please try again.');
            console.error(err);
        });
    };

    const handleAddToBestService = (child) => {
        const payload = {
            name: child?.name,
            description: child?.childDesc || '',
            price: child?.price || 0,
            member_price: child?.member_price || 0,
            incentive: child?.incentive || 0,
            gender: child?.gender || '',
            img: child?.img || '',
            staffIds: child?.staffIds?.map(s => s?._id || s) || [],
            unitIds: getSelectedUnit()?._id,
            products: child?.steps?.flatMap(step =>
                step?.products?.map(p => ({
                    product: p?.product,
                    quantity: parseFloat(p?.quantity) || 0,
                    unit: p?.productType || ''
                })) || []
            ) || [],
            miscItems: [],
            service_time: child?.steps?.reduce((acc, step) => acc + parseFloat(step?.service_time || 0), 0).toString() || '0',
            serviceMargin: 0
        };

        HitApi(payload, addBestService).then((res) => {
            if (res?.statusCode === 201 || res?.statusCode === 200 || res?.success) {
                toast.success('Added to Best Services!');
                // Add to tracking set
                setBestServiceNames((prev) => new Set([...prev, child?.name?.toLowerCase()?.trim()]));
            } else {
                toast.error(res?.message || 'Failed to add to Best Services');
            }
        }).catch((err) => {
            toast.error('Error adding to Best Services. Please try again.');
            console.error(err);
        });
    };

    const confirmDelete = () => {
        setIsDeleting(true);
        const json = { _id: deletingRow?._id };

        HitApi(json, deleteParent).then((res) => {
            if (res?.success || res?.message?.includes('deleted successfully')) {
                toast.success('Service group deleted successfully!');
                getServiceGroup();

                if (selectedParent?._id === deletingRow?._id) {
                    setChildService(null);
                    setSelectedParent(null);
                    setParentId('');
                }
            } else {
                toast.error(res?.message || 'Failed to delete service group');
            }
        }).catch((err) => {
            toast.error('Error occurred while deleting service group. Please try again.');
            console.error(err);
        }).finally(() => {
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
            setDeletingRow(null);
        });
    };

    const handleDeleteChild = (row) => {
        const serviceName = row.name || 'this child service';

        if (window.confirm(`Are you sure you want to delete ${serviceName}?`)) {
            const json = { _id: row?._id };
            HitApi(json, deleteChild).then((res) => {
                if (res?.success || res?.message?.includes('deleted successfully')) {
                    toast.success('Child service deleted successfully!');
                    showSelectedChild(selectedParent);
                } else {
                    toast.error(res?.message || 'Failed to delete child service');
                }
            }).catch((err) => {
                toast.error('Error occurred while deleting child service. Please try again.');
                console.error(err);
            });
        }
    };

    const getRandomColor = (index) => {
        const colors = [
            'border-l-blue-500', 'border-l-green-500', 'border-l-yellow-500',
            'border-l-red-500', 'border-l-purple-500', 'border-l-pink-500',
            'border-l-indigo-500', 'border-l-teal-500'
        ];
        return colors[index % colors.length];
    };

    const formatServiceTime = (steps) => {
        if (!steps || steps.length === 0) return '00:00';
        const totalTime = steps.reduce((acc, step) => acc + parseFloat(step.service_time || 0), 0);
        const hours = Math.floor(totalTime);
        const minutes = Math.round((totalTime - hours) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const NullApiJsonORErrorJson = () => {
        dispatch(setApiJson(null));
        dispatch(setApiErrorJson(null));
    };

    // Export All Services (Parents with their Children)
    const handleExportAllServices = async (format) => {
        setIsExportDropdownOpen(false);

        try {
            toast.loading('Preparing export...');

            // Fetch all parent services
            const parentJson = {
                page: 1,
                limit: 100000,
                search: {
                    unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
                }
            };

            const parentRes = await HitApi(parentJson, searchParent);

            if (!parentRes?.data?.docs || parentRes.data.docs.length === 0) {
                toast.dismiss();
                toast.error('No service groups to export');
                return;
            }

            const allParents = parentRes.data.docs;
            const exportData = [];
            let serialNumber = 1;

            // Fetch children for each parent
            for (const parent of allParents) {
                const childJson = {
                    page: 1,
                    limit: 100000,
                    search: { parentId: parent._id }
                };

                const childRes = await HitApi(childJson, searchChild);
                const children = childRes?.data?.docs || [];

                if (children.length > 0) {
                    // Add each child with parent info
                    children.forEach((child) => {
                        exportData.push({
                            serialNumber: serialNumber++,
                            groupName: parent.name || 'N/A',
                            serviceName: child.name || 'N/A',
                            price: child.price ? `Rs. ${Number(child.price).toFixed(2)}` : 'Rs. 0.00',
                            duration: formatServiceTime(child.steps),
                            status: child.status || 'Active',
                            createdAt: child.createdAt ? new Date(child.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric'
                            }) : 'N/A'
                        });
                    });
                } else {
                    // Add parent with no services
                    exportData.push({
                        serialNumber: serialNumber++,
                        groupName: parent.name || 'N/A',
                        serviceName: 'No services',
                        price: '-',
                        duration: '-',
                        status: parent.status || 'Active',
                        createdAt: parent.createdAt ? new Date(parent.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                        }) : 'N/A'
                    });
                }
            }

            if (exportData.length === 0) {
                toast.dismiss();
                toast.error('No data to export');
                return;
            }

            const exportHeaders = [
                { title: 'S.No', key: 'serialNumber' },
                { title: 'Group Name', key: 'groupName' },
                { title: 'Service Name', key: 'serviceName' },
                { title: 'Price', key: 'price' },
                { title: 'Duration', key: 'duration' },
                { title: 'Status', key: 'status' },
                { title: 'Created At', key: 'createdAt' }
            ];

            const filename = `all_services_export_${new Date().toISOString().split('T')[0]}`;

            toast.dismiss();

            if (format === 'csv') {
                exportToCSV(exportData, exportHeaders, `${filename}.csv`);
                toast.success(`Exported ${exportData.length} services to CSV`);
            } else if (format === 'pdf') {
                exportToPDF(exportData, exportHeaders, `${filename}.pdf`, 'All Services Report');
                toast.success(`Exported ${exportData.length} services to PDF`);
            }
        } catch (error) {
            toast.dismiss();
            toast.error('Error exporting data');
            console.error('Export error:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-5">
            <div className="flex items-center justify-between mb-4">
                <PageHeader title={'Services Management'} description={'View and manage service categories'} />

                {/* Export Data Dropdown */}
                <div className="relative" ref={exportDropdownRef}>
                    <button
                        onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700 font-medium"
                    >
                        <Download size={16} />
                        Export Data
                        <ChevronDown size={16} className={`transition-transform ${isExportDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isExportDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            <button
                                onClick={() => handleExportAllServices('csv')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-gray-700"
                            >
                                <FileSpreadsheet size={18} className="text-green-600" />
                                Export as CSV
                            </button>
                            <button
                                onClick={() => handleExportAllServices('pdf')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-gray-700 border-t border-gray-100"
                            >
                                <FileText size={18} className="text-red-600" />
                                Export as PDF
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <div className="w-80 bg-white border-r min-h-screen">
                    <div className="p-4 border-b bg-gray-50">
                        {canAddService && (
                            <NavLink to="/addParent">
                                <button className="w-full flex items-center justify-center gap-2 bg-black text-white px-4 py-2 rounded-lg">
                                    <CirclePlus size={16} />
                                    Add Group
                                </button>
                            </NavLink>
                        )}
                    </div>

                    <div className="p-2">
                        {parentService?.map((parent, index) => (
                            <div
                                key={parent._id}
                                className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${selectedParent?._id === parent._id
                                    ? 'bg-blue-50 border-l-4 border-black'
                                    : 'hover:bg-gray-50'
                                    }`}
                                onClick={() => showSelectedChild(parent)}
                            >
                                <div className="flex items-center gap-3">
                                    <Menu size={16} className="text-gray-400" />
                                    <span className="font-medium text-gray-900">{parent.name}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    {/* Tick always visible when added */}
                                    {salonParentNames.has(parent.name?.toLowerCase()?.trim()) && (
                                        <AddSalonGroupButton onAdd={() => handleAddToSalonGroup(parent)} tooltipText="Add to Salon Group" isAdded={true} />
                                    )}
                                    {kiosParentNames.has(parent.name?.toLowerCase()?.trim()) && (
                                        <AddKiosGroupButton onAdd={() => handleAddToKiosGroup(parent)} tooltipText="Add to Kios Group" isAdded={true} />
                                    )}
                                    {atHomeParentNames.has(parent.name?.toLowerCase()?.trim()) && (
                                        <AddAtHomeGroupButton onAdd={() => handleAddToAtHomeGroup(parent)} tooltipText="Add to Home Service Group" isAdded={true} />
                                    )}
                                    {/* These appear on hover */}
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                        {!salonParentNames.has(parent.name?.toLowerCase()?.trim()) && (
                                            <AddSalonGroupButton onAdd={() => handleAddToSalonGroup(parent)} tooltipText="Add to Salon Group" isAdded={false} />
                                        )}
                                        {!kiosParentNames.has(parent.name?.toLowerCase()?.trim()) && (
                                            <AddKiosGroupButton onAdd={() => handleAddToKiosGroup(parent)} tooltipText="Add to Kios Group" isAdded={false} />
                                        )}
                                        {!atHomeParentNames.has(parent.name?.toLowerCase()?.trim()) && (
                                            <AddAtHomeGroupButton onAdd={() => handleAddToAtHomeGroup(parent)} tooltipText="Add to Home Service Group" isAdded={false} />
                                        )}
                                        {canEditSerive && (
                                            <EditButton onEdit={() => handleEdit(parent)} itemName="Group service" />
                                        )}
                                        {canDeleteService && (
                                            <DeleteButton onDelete={() => handleDelete(parent)} itemName="Group service" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className='flex justify-end p-3'>
                            <GroupPagination />
                        </div>
                    </div>
                </div>

                {/* Right Panel */}
                <div className="flex-1 bg-white">
                    {childService ? (
                        <div>
                            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {selectedParent?.name} Services
                                </h2>
                                {canAddService && (
                                    <NavLink to={`/addChild/${parentId}`} onClick={NullApiJsonORErrorJson}>
                                        <button className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg">
                                            <CirclePlus size={16} />
                                            Add Service
                                        </button>
                                    </NavLink>
                                )}
                            </div>

                            <div className="p-6 space-y-2">
                                {childService.length > 0 ? (
                                    childService.map((child, index) => (
                                        <div
                                            key={child._id}
                                            className={`group flex items-center justify-between p-4 rounded-lg border-l-4 ${getRandomColor(index)} bg-gray-50 hover:bg-gray-100`}
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="font-medium text-gray-900">{child.name}</h3>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right">
                                                            <div className="font-semibold text-gray-900">
                                                                ₹{child.price?.toLocaleString() || '0'}
                                                            </div>
                                                            {child.member_price && (
                                                                <div className="text-sm text-gray-500">
                                                                    Member: ₹{child.member_price.toLocaleString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                                            <Clock size={14} />
                                                            <span>{formatServiceTime(child.steps)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {/* Tick always visible when added */}
                                                            {salonParentNames.has(selectedParent?.name?.toLowerCase()?.trim()) && salonChildIds.has(child?.name?.toLowerCase()?.trim()) && (
                                                                <AddSalonChildButton
                                                                    onAdd={() => handleAddToSalonChild(child)}
                                                                    tooltipText="Add to Salon Child"
                                                                    isAdded={true}
                                                                />
                                                            )}
                                                            {kiosParentNames.has(selectedParent?.name?.toLowerCase()?.trim()) && kiosChildIds.has(child?.name?.toLowerCase()?.trim()) && (
                                                                <AddKiosChildButton
                                                                    onAdd={() => handleAddToKiosChild(child)}
                                                                    tooltipText="Add to Kios Child"
                                                                    isAdded={true}
                                                                />
                                                            )}
                                                            {atHomeParentNames.has(selectedParent?.name?.toLowerCase()?.trim()) && atHomeChildIds.has(child?.name?.toLowerCase()?.trim()) && (
                                                                <AddAtHomeChildButton
                                                                    onAdd={() => handleAddToAtHomeChild(child)}
                                                                    tooltipText="Add to Home Service Child"
                                                                    isAdded={true}
                                                                />
                                                            )}
                                                            {/* Star highlighted if already in best services */}
                                                            {bestServiceNames.has(child?.name?.toLowerCase()?.trim()) && (
                                                                <button
                                                                    className="p-2 rounded-lg bg-yellow-400 transition-colors"
                                                                    title="Already in Best Services"
                                                                >
                                                                    <Star size={16} className="text-white fill-white" />
                                                                </button>
                                                            )}
                                                            {/* These appear on hover */}
                                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                                                {!bestServiceNames.has(child?.name?.toLowerCase()?.trim()) && (
                                                                    <button
                                                                        onClick={() => handleAddToBestService(child)}
                                                                        className="p-2 rounded-lg bg-yellow-100 hover:bg-yellow-200 transition-colors"
                                                                        title="Add to Best Services"
                                                                    >
                                                                        <Star size={16} className="text-yellow-600" />
                                                                    </button>
                                                                )}
                                                                {salonParentNames.has(selectedParent?.name?.toLowerCase()?.trim()) && !salonChildIds.has(child?.name?.toLowerCase()?.trim()) && (
                                                                    <AddSalonChildButton
                                                                        onAdd={() => handleAddToSalonChild(child)}
                                                                        tooltipText="Add to Salon Child"
                                                                        isAdded={false}
                                                                    />
                                                                )}
                                                                {kiosParentNames.has(selectedParent?.name?.toLowerCase()?.trim()) && !kiosChildIds.has(child?.name?.toLowerCase()?.trim()) && (
                                                                    <AddKiosChildButton
                                                                        onAdd={() => handleAddToKiosChild(child)}
                                                                        tooltipText="Add to Kios Child"
                                                                        isAdded={false}
                                                                    />
                                                                )}
                                                                {atHomeParentNames.has(selectedParent?.name?.toLowerCase()?.trim()) && !atHomeChildIds.has(child?.name?.toLowerCase()?.trim()) && (
                                                                    <AddAtHomeChildButton
                                                                        onAdd={() => handleAddToAtHomeChild(child)}
                                                                        tooltipText="Add to Home Service Child"
                                                                        isAdded={false}
                                                                    />
                                                                )}
                                                                {canEditSerive && (
                                                                    <EditButton onEdit={() => handleEditChild(child)} itemName="Service" />
                                                                )}
                                                                {canDeleteService && (
                                                                    <DeleteButton onDelete={() => handleDeleteChild(child)} itemName="Service" />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-600">No services found in this category</div>
                                )}
                            </div>

                            <div className="p-4 flex justify-end">
                                <ServicePagination />
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-center text-gray-600">
                            Select a service group to view services
                        </div>
                    )}
                </div>
            </div>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingRow(null);
                }}
                onConfirm={confirmDelete}
                itemName={deletingRow?.name}
                itemType="service group"
                isLoading={isDeleting}
                warningMessage="This will also delete all child services related to this parent."
            />
        </div>
    );
}

export default Services;
